import { FC, useEffect, useMemo } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import {
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import {
  IAcademicsSchedule,
  EScheduleType,
  ETerm,
  ISchedulePeriod,
  EWeekDay,
  EBreakType,
} from '../../../../../apis/academics/academicsSchedules.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import {
  useAcademicsSchedulesContext,
  useAcademicsSchedulesActions,
} from '../../contexts/academicsSchedules.context';
import { useAcademicsService } from '../../../../../apis/academics/useAcademicsService';
import { useAcademicsClassesAndCoursesState } from '../../contexts/academicsClassesAndCourses.context';
import { useStaffListHook } from '../../../staff/hooks/useStaffListHook';
import { zNumberFromInput } from 'src/utils/zodFormHelpers';

type PeriodFormData = {
  id?: string;
  periodNumber: number;
  startTime: string;
  endTime: string;
  dayOfWeek: EWeekDay;
  courseId?: number | string;
  teacherId?: number | string;
  classId?: number | string;
  room?: string;
  isBreak?: boolean;
  breakType?: EBreakType;
};

type ScheduleFormData = {
  name: string;
  description?: string;
  type: EScheduleType;
  academicYear: string;
  term?: ETerm;
  isActive: boolean;
  periods?: PeriodFormData[];
};

const getPeriodFormSchema = (
  translateText: (key: string) => string
): ZodType<PeriodFormData> =>
  z.object({
    id: z.string().optional(),
    periodNumber: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    startTime: z.string().min(1, translateText('common.fieldRequired')),
    endTime: z.string().min(1, translateText('common.fieldRequired')),
    dayOfWeek: z.nativeEnum(EWeekDay, {
      required_error: translateText('common.fieldRequired'),
    }),
    courseId: z.union([z.number(), z.string()]).optional(),
    teacherId: z.union([z.number(), z.string()]).optional(),
    classId: z.union([z.number(), z.string()]).optional(),
    room: z.string().optional(),
    isBreak: z.boolean().optional(),
    breakType: z.nativeEnum(EBreakType).optional(),
  });

const getScheduleFormSchema = (
  translateText: (key: string) => string
): ZodType<ScheduleFormData> =>
  z.object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().optional(),
    type: z.nativeEnum(EScheduleType, {
      required_error: translateText('common.fieldRequired'),
    }),
    academicYear: z.string().min(1, translateText('common.fieldRequired')),
    term: z.nativeEnum(ETerm).optional(),
    isActive: z.boolean(),
    periods: z.array(getPeriodFormSchema(translateText)).default([]),
  });

const getScheduleTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: EScheduleType.REGULAR,
      value:
        translateText('academics.schedules.types.regular') ||
        EScheduleType.REGULAR,
    },
    {
      key: EScheduleType.EXAM,
      value:
        translateText('academics.schedules.types.exam') || EScheduleType.EXAM,
    },
    {
      key: EScheduleType.SPECIAL_EVENT,
      value:
        translateText('academics.schedules.types.specialEvent') ||
        EScheduleType.SPECIAL_EVENT,
    },
    {
      key: EScheduleType.HOLIDAY,
      value:
        translateText('academics.schedules.types.holiday') ||
        EScheduleType.HOLIDAY,
    },
  ];

const getTermOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: ETerm.TERM_1,
      value: translateText('academics.schedules.terms.term1') || ETerm.TERM_1,
    },
    {
      key: ETerm.TERM_2,
      value: translateText('academics.schedules.terms.term2') || ETerm.TERM_2,
    },
    {
      key: ETerm.TERM_3,
      value: translateText('academics.schedules.terms.term3') || ETerm.TERM_3,
    },
    {
      key: ETerm.SEMESTER_1,
      value:
        translateText('academics.schedules.terms.semester1') || ETerm.SEMESTER_1,
    },
    {
      key: ETerm.SEMESTER_2,
      value:
        translateText('academics.schedules.terms.semester2') || ETerm.SEMESTER_2,
    },
    {
      key: ETerm.ANNUAL,
      value: translateText('academics.schedules.terms.annual') || ETerm.ANNUAL,
    },
  ];

const getWeekDayOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: EWeekDay.MONDAY,
      value:
        translateText('academics.schedules.weekDays.monday') || EWeekDay.MONDAY,
    },
    {
      key: EWeekDay.TUESDAY,
      value:
        translateText('academics.schedules.weekDays.tuesday') || EWeekDay.TUESDAY,
    },
    {
      key: EWeekDay.WEDNESDAY,
      value:
        translateText('academics.schedules.weekDays.wednesday') ||
        EWeekDay.WEDNESDAY,
    },
    {
      key: EWeekDay.THURSDAY,
      value:
        translateText('academics.schedules.weekDays.thursday') ||
        EWeekDay.THURSDAY,
    },
    {
      key: EWeekDay.FRIDAY,
      value:
        translateText('academics.schedules.weekDays.friday') || EWeekDay.FRIDAY,
    },
    {
      key: EWeekDay.SATURDAY,
      value:
        translateText('academics.schedules.weekDays.saturday') ||
        EWeekDay.SATURDAY,
    },
    {
      key: EWeekDay.SUNDAY,
      value:
        translateText('academics.schedules.weekDays.sunday') || EWeekDay.SUNDAY,
    },
  ];

const getBreakTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: EBreakType.LUNCH,
      value:
        translateText('academics.schedules.breakTypes.lunch') || EBreakType.LUNCH,
    },
    {
      key: EBreakType.SHORT_BREAK,
      value:
        translateText('academics.schedules.breakTypes.shortBreak') ||
        EBreakType.SHORT_BREAK,
    },
    {
      key: EBreakType.ASSEMBLY,
      value:
        translateText('academics.schedules.breakTypes.assembly') ||
        EBreakType.ASSEMBLY,
    },
    {
      key: EBreakType.RECESS,
      value:
        translateText('academics.schedules.breakTypes.recess') ||
        EBreakType.RECESS,
    },
  ];

const AddEditScheduleView: FC = () => {
  const { translateText } = useTranslation();
  const scheduleFormSchema = getScheduleFormSchema(translateText);

  const {
    academicsSchedulesAppState: {
      selectedSchedule,
      isManageScheduleOpen,
      isScheduleLoading,
      scheduleErrorMessage,
      isCreateMode,
      isEditMode,
    },
    refetchSchedules,
  } = useAcademicsSchedulesContext();

  const {
    setIsManageScheduleOpen,
    setIsScheduleLoading,
    setIsScheduleError,
    setScheduleErrorMessage,
  } = useAcademicsSchedulesActions();

  const { createAcademicsSchedule, patchAcademicsSchedule } =
    useAcademicsService();

  const { academicsClassesAndCoursesAppState } =
    useAcademicsClassesAndCoursesState();
  const { data: staffListResponse } = useStaffListHook({
    page: 1,
    limit: 1000,
  });

  const classOptions = useMemo<TKeyValue<string | number>[]>(() => {
    return academicsClassesAndCoursesAppState.classes.map((cls) => ({
      key: cls.id,
      value: cls.name,
    }));
  }, [academicsClassesAndCoursesAppState.classes]);

  const courseOptions = useMemo<TKeyValue<string | number>[]>(() => {
    return academicsClassesAndCoursesAppState.courses.map((course) => ({
      key: course.id,
      value: `${course.name} (${course.subjectCode})`,
    }));
  }, [academicsClassesAndCoursesAppState.courses]);

  const teacherOptions = useMemo<TKeyValue<string | number>[]>(() => {
    if (!staffListResponse?.data) return [];
    return staffListResponse.data
      .map((staff) => {
        const parsedId = parseInt(staff.id, 10);
        if (isNaN(parsedId)) {
          return null;
        }
        return {
          key: parsedId,
          value:
            `${staff.basicDetails.firstName} ${staff.basicDetails.middleName || ''} ${staff.basicDetails.lastName}`.trim(),
        } as TKeyValue<string | number>;
      })
      .filter((item) => item !== null) as TKeyValue<string | number>[];
  }, [staffListResponse]);

  const defaultValues: ScheduleFormData = {
    name: '',
    description: '',
    type: EScheduleType.REGULAR,
    academicYear: '',
    term: undefined,
    isActive: true,
    periods: [],
  };

  const methods = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    register,
    watch,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'periods',
  });

  useEffect(() => {
    if (selectedSchedule && isManageScheduleOpen && isEditMode) {
      reset({
        name: selectedSchedule.name || '',
        description: selectedSchedule.description || '',
        type: selectedSchedule.type || EScheduleType.REGULAR,
        academicYear: selectedSchedule.academicYear || '',
        term: selectedSchedule.term,
        isActive: selectedSchedule.isActive ?? true,
        periods:
          selectedSchedule.periods?.map((period) => ({
            id: period.id,
            periodNumber: period.periodNumber,
            startTime: period.startTime,
            endTime: period.endTime,
            dayOfWeek: period.dayOfWeek,
            courseId: period.courseId ? Number(period.courseId) : 'select',
            teacherId: period.teacherId ? Number(period.teacherId) : 'select',
            classId: period.classId ? Number(period.classId) : 'select',
            room: period.room ?? '',
            isBreak: period.isBreak || false,
            breakType: period.breakType ?? undefined,
          })) || [],
      });
    } else if (isManageScheduleOpen && isCreateMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchedule, isManageScheduleOpen, isCreateMode, isEditMode, reset]);

  const handleClose = () => {
    setIsManageScheduleOpen(false);
    setIsScheduleError(false);
    setScheduleErrorMessage('');
    reset(defaultValues);
  };

  const handleAddPeriod = () => {
    append({
      periodNumber: fields.length + 1,
      startTime: '',
      endTime: '',
      dayOfWeek: EWeekDay.MONDAY,
      courseId: 'select',
      teacherId: 'select',
      classId: 'select',
      room: '',
      isBreak: false,
      breakType: undefined,
    });
  };

  const onSubmitForm = async (data: ScheduleFormData) => {
    setIsScheduleLoading(true);
    setIsScheduleError(false);
    setScheduleErrorMessage('');

    try {
      const periods: ISchedulePeriod[] = (
        (data.periods || []) as PeriodFormData[]
      ).map((period, index) => ({
        id: period.id || `${selectedSchedule?.id || 'new'}_period_${index}`,
        periodNumber: period.periodNumber,
        startTime: period.startTime,
        endTime: period.endTime,
        dayOfWeek: period.dayOfWeek,
        courseId:
          period.courseId === 'select'
            ? undefined
            : typeof period.courseId === 'number'
              ? period.courseId
              : Number(period.courseId),
        teacherId:
          period.teacherId === 'select'
            ? undefined
            : typeof period.teacherId === 'number'
              ? period.teacherId
              : Number(period.teacherId),
        classId:
          period.classId === 'select'
            ? undefined
            : typeof period.classId === 'number'
              ? period.classId
              : Number(period.classId),
        room: period.room,
        isBreak: period.isBreak || false,
        breakType: period.breakType,
      }));

      const scheduleData: IAcademicsSchedule = {
        id: selectedSchedule?.id || '',
        name: data.name,
        description: data.description,
        type: data.type,
        academicYear: data.academicYear,
        term: data.term,
        isActive: data.isActive,
        createdAt: selectedSchedule?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        periods,
        instituteId: '00000000-0000-0000-0000-000000000000',
        classId: '00000000-0000-0000-0000-000000000000',
      };

      if (isEditMode && selectedSchedule?.id) {
        await patchAcademicsSchedule(scheduleData);
      } else {
        await createAcademicsSchedule(scheduleData);
      }

      refetchSchedules();
      handleClose();
    } catch (error: any) {
      setIsScheduleError(true);
      setScheduleErrorMessage(
        error?.message || translateText('common.error') || 'An error occurred'
      );
    } finally {
      setIsScheduleLoading(false);
    }
  };

  return (
    <div data-testid="add-edit-schedule-container">
      <StructuredModal
        isOpen={isManageScheduleOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText('academics.schedules.editSchedule') ||
            'Edit Schedule'
            : translateText('academics.schedules.addSchedule') || 'Add Schedule'
        }
        drawerWidth="85vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isScheduleLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          scheduleErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="schedule-error-message"
            >
              {scheduleErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="schedule-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              padding={{ xs: 1, sm: 2 }}
              data-testid="schedule-form-container"
            >
              <Grid item xs={12} data-testid="schedule-details-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {translateText('academics.schedules.scheduleDetails') ||
                    'Schedule Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="schedule-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="name"
                  id="schedule-name"
                  label={
                    translateText('academics.schedules.columns.scheduleName') ||
                    'Schedule Name'
                  }
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="schedule-type-field">
                <ControlledSelectField
                  control={control}
                  name="type"
                  id="schedule-type"
                  label={
                    translateText('academics.schedules.columns.type') || 'Type'
                  }
                  itemList={getScheduleTypeOptions(translateText)}
                  errorMessage={errors.type?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="schedule-description-field">
                <ControlledTextField
                  control={control}
                  name="description"
                  id="schedule-description"
                  label={translateText('common.description') || 'Description'}
                  errorMessage={errors.description?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="schedule-academic-year-field"
              >
                <ControlledTextField
                  control={control}
                  name="academicYear"
                  id="schedule-academic-year"
                  label={
                    translateText('academics.schedules.columns.academicYear') ||
                    'Academic Year'
                  }
                  errorMessage={errors.academicYear?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="schedule-term-field">
                <ControlledSelectField
                  control={control}
                  name="term"
                  id="schedule-term"
                  label={
                    translateText('academics.schedules.columns.term') || 'Term'
                  }
                  itemList={getTermOptions(translateText)}
                  errorMessage={errors.term?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="schedule-active-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('isActive')}
                      data-testid="schedule-is-active-checkbox"
                    />
                  }
                  label={
                    translateText('academics.schedules.isActive') || 'Active'
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12} data-testid="periods-section">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {translateText('academics.schedules.periods') || 'Periods'}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleAddPeriod}
                    data-testid="add-period-button"
                    size="small"
                    sx={{
                      minHeight: { xs: '44px', sm: '32px' },
                      fontSize: { xs: '16px', sm: '14px' },
                    }}
                  >
                    {translateText('academics.schedules.addPeriod') ||
                      'Add Period'}
                  </Button>
                </Box>
              </Grid>

              {fields.map((field, index) => {
                const periodErrors = errors.periods?.[index];
                const isBreak = watch(`periods.${index}.isBreak`);

                return (
                  <Grid
                    item
                    xs={12}
                    key={field.id}
                    data-testid={`period-${index}-container`}
                  >
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Typography variant="subtitle1">
                          {translateText('academics.schedules.period') ||
                            'Period'}{' '}
                          {index + 1}
                        </Typography>
                        <IconButton
                          onClick={() => remove(index)}
                          data-testid={`remove-period-${index}-button`}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>

                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12} sm={6} md={2}>
                          <ControlledTextField
                            control={control}
                            name={`periods.${index}.periodNumber`}
                            id={`period-${index}-number`}
                            label={
                              translateText(
                                'academics.schedules.columns.period'
                              ) || 'Period Number'
                            }
                            errorMessage={periodErrors?.periodNumber?.message}
                            required
                            type="number"
                            isGridItem={false}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <ControlledSelectField
                            control={control}
                            name={`periods.${index}.dayOfWeek`}
                            id={`period-${index}-day`}
                            label={
                              translateText(
                                'academics.schedules.columns.day'
                              ) || 'Day'
                            }
                            itemList={getWeekDayOptions(translateText)}
                            errorMessage={periodErrors?.dayOfWeek?.message}
                            isRequired
                            isGridItem={false}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <ControlledTextField
                            control={control}
                            name={`periods.${index}.startTime`}
                            id={`period-${index}-start-time`}
                            label={
                              translateText('academics.schedules.startTime') ||
                              'Start Time'
                            }
                            errorMessage={periodErrors?.startTime?.message}
                            required
                            type="time"
                            isGridItem={false}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <ControlledTextField
                            control={control}
                            name={`periods.${index}.endTime`}
                            id={`period-${index}-end-time`}
                            label={
                              translateText('academics.schedules.endTime') ||
                              'End Time'
                            }
                            errorMessage={periodErrors?.endTime?.message}
                            required
                            type="time"
                            isGridItem={false}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...register(`periods.${index}.isBreak`)}
                                data-testid={`period-${index}-is-break-checkbox`}
                              />
                            }
                            label={
                              translateText(
                                'academics.schedules.columns.break'
                              ) || 'Break'
                            }
                          />
                        </Grid>

                        {isBreak && (
                          <Grid item xs={12} sm={6} md={2}>
                            <ControlledSelectField
                              control={control}
                              name={`periods.${index}.breakType`}
                              id={`period-${index}-break-type`}
                              label={
                                translateText(
                                  'academics.schedules.breakType'
                                ) || 'Break Type'
                              }
                              itemList={getBreakTypeOptions(translateText)}
                              errorMessage={periodErrors?.breakType?.message}
                              isGridItem={false}
                            />
                          </Grid>
                        )}

                        {!isBreak && (
                          <>
                            <Grid item xs={12} sm={6} md={3}>
                              <ControlledSelectField
                                control={control}
                                name={`periods.${index}.classId`}
                                id={`period-${index}-class`}
                                label={
                                  translateText(
                                    'academics.schedules.columns.class'
                                  ) || 'Class'
                                }
                                itemList={classOptions}
                                errorMessage={periodErrors?.classId?.message}
                                isGridItem={false}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <ControlledSelectField
                                control={control}
                                name={`periods.${index}.courseId`}
                                id={`period-${index}-course`}
                                label={
                                  translateText(
                                    'academics.schedules.columns.course'
                                  ) || 'Course'
                                }
                                itemList={courseOptions}
                                errorMessage={periodErrors?.courseId?.message}
                                isGridItem={false}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <ControlledSelectField
                                control={control}
                                name={`periods.${index}.teacherId`}
                                id={`period-${index}-teacher`}
                                label={
                                  translateText(
                                    'academics.schedules.columns.teacher'
                                  ) || 'Teacher'
                                }
                                itemList={teacherOptions}
                                errorMessage={periodErrors?.teacherId?.message}
                                isGridItem={false}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <ControlledTextField
                                control={control}
                                name={`periods.${index}.room`}
                                id={`period-${index}-room`}
                                label={
                                  translateText(
                                    'academics.schedules.columns.room'
                                  ) || 'Room'
                                }
                                errorMessage={periodErrors?.room?.message}
                                isGridItem={false}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}

              {fields.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('academics.schedules.noPeriods') ||
                      'No periods added. Click "Add Period" to add one.'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditScheduleView;
